import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { EntrySummaryModel, InvoiceModel, LineModel } from '@data-layer/models/entry-summaries';
import {
  BondModel,
  BondRequestModel,
  ForeignPortModel,
  LocationOfGoodsModel,
  PortModel,
  ShipmentModel,
  StateModel,
  ValidationMessageModel,
} from '@data-layer/models/common';
import {
  enumEntrySummaryDutiesPaymentType,
  enumEntrySummaryState,
  enumEntrySummarySubmissionType,
  enumEntrySummaryType,
  enumTransportationMode,
} from '@data-layer/enums/entry-summaries';
import {
  ACH_METHOD_NOT_ALLOWED_WITH_PUN,
  ACH_METHOD_NOT_ALLOWED_WITHOUT_PUN,
  ARRIVAL_DATE_MISSING,
  ARRIVAL_DATE_TOO_EARLY,
  BOND_REFERENCE_NUMBER_MALFORMED,
  BOND_REQUEST_NOT_ALLOWED_FOR_INFORMAL_ENTRY,
  BOND_SURETY_CODE_MISSING,
  BOND_TYPE_MISSING,
  EXPORT_DATE_LATER_THAN_ARRIVAL_DATE,
  EXPORT_DATE_MISSING,
  FLIGHT_NUMBER_MISSING,
  FOREIGN_PORT_MISSING,
  FORMAL_ENTRY_REQUIRED_FOR_CBP_ASSIGNED_NUMBER,
  FORMAL_SHIPMENT_TYPE_CN_ENTERED_VALUE_TOO_SMALL,
  FORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_SMALL,
  FORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_SMALL_DESKTOP,
  INFORMAL_SHIPMENT_TYPE_CN_ENTERED_VALUE_TOO_BIG,
  INFORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG,
  INFORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG_DESKTOP,
  INFORMAL_NON_DRAFT_ES_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG_DESKTOP,
  LOCATION_MISSING,
  PAYER_UNIT_NUMBER_MALFORMED,
  PAYER_UNIT_NUMBER_MISSING,
  PORT_OF_ENTRY_MISSING,
  PORT_OF_UNLADING_MISSING,
  STATE_OF_DESTINATION_MISSING,
  TRIP_MISSING,
  VESSEL_NAME_MISSING,
  VESSEL_NAME_TOO_LONG,
} from '@constants/entity-validation-messages';
import { isCbpAssignedNumberImporter } from '@helpers/importer.helper';
import { Router } from '@angular/router';
import { CodeRepository } from '@data-layer/repositories';
import { forkJoin, ReplaySubject } from 'rxjs';
import { CompanyModel } from '@data-layer/models/companies';
import { CustomState, enumBondRequestState, enumBondType } from '@data-layer/enums/common';
import { EntrySummaryService } from '@services/entry-summary.service';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { CHINA_COUNTRY_CODE, ENTERED_VALUE_CHINA_LIMIT, ENTERED_VALUE_TOTAL_LIMIT } from '@constants/constants';
import { EntityModel } from '@data-layer/models/entities';
import { CompaniesService } from '@services/companies.service';
import { flatten } from 'lodash-es';
import { UtilsHelper } from '@helpers/utils-helper';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { UserBetaFlagModel } from '@app/data-layer/models/user';

@Component({
  selector: 'es-shipment-summary-desktop',
  templateUrl: './es-shipment-summary-desktop.component.html',
  styleUrls: ['./es-shipment-summary-desktop.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsShipmentSummaryDesktopComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public allValidationMessages: ValidationMessageModel[] = [];
  @Input() public isCanEdit: boolean;
  // Granular per-pencil overrides (nullable — fall back to isCanEdit when unbound). Lets the
  // broker review page enable ONLY the shipment-field pencils while isCanEdit stays false.
  @Input() public canEditDetails: boolean;
  @Input() public canEditFrom: boolean;
  @Input() public canEditTo: boolean;
  @Input() public canEditVia: boolean;
  @Input() public canEditShipmentType: boolean;
  /**
   * #13261: whether the details sidebar offers the Truck tile (beta flag). Gates the Details pencil
   * for a Truck ES — without the tile the sidebar could only switch the entry away from Truck.
   */
  @Input() public isTruckModeAvailable: boolean = false;
  @Input() public company: CompanyModel;
  @Input() public isShowFirstLine: boolean = true;
  @Input() public isBodyOpen: boolean = true;
  @Input() public isShowType: boolean = true;
  @Input() public isShowBR: boolean;

  @Output() public editDetails: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editFrom: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editTo: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editVia: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editMF: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editShipmentType: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editDutiesPaymentType: EventEmitter<void> = new EventEmitter<void>();

  public exportDate: string = null;
  public foreignPort: ForeignPortModel = null;
  public location: LocationOfGoodsModel = null;
  public stateOfDestination: StateModel = null;
  public isEmpty: boolean = false;
  public currentCardValidationMessages: ValidationMessageModel[] = [];
  public enumEntrySummaryType: typeof enumEntrySummaryType = enumEntrySummaryType;
  public enumEntrySummarySubmissionType: typeof enumEntrySummarySubmissionType = enumEntrySummarySubmissionType;
  public enumEntrySummaryDutiesPaymentType: typeof enumEntrySummaryDutiesPaymentType = enumEntrySummaryDutiesPaymentType;
  public enumBondType: typeof enumBondType = enumBondType;
  public enumEntityModelCode: typeof enumEntityModelCode = enumEntityModelCode;
  public DEFAULT_CURRENCY_CODE: typeof DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;
  public enumBondRequestState: typeof enumBondRequestState = enumBondRequestState;

  public bond: BondModel;
  public bondRequest: BondRequestModel;
  public arrivalPort: PortModel;
  public bondTypeLabel: string = '';
  public isSingleBondType: boolean = false;

  public enteredValue: number;

  private readonly missingFields: string[] = [
    ARRIVAL_DATE_MISSING,
    LOCATION_MISSING,
    PORT_OF_ENTRY_MISSING,
    PORT_OF_UNLADING_MISSING,
    TRIP_MISSING,
    FLIGHT_NUMBER_MISSING,
    VESSEL_NAME_MISSING,
    VESSEL_NAME_TOO_LONG,
    PAYER_UNIT_NUMBER_MISSING,
    PAYER_UNIT_NUMBER_MALFORMED,
    STATE_OF_DESTINATION_MISSING,
    FORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_SMALL,
    INFORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG,
    FORMAL_SHIPMENT_TYPE_CN_ENTERED_VALUE_TOO_SMALL,
    INFORMAL_SHIPMENT_TYPE_CN_ENTERED_VALUE_TOO_BIG,
    BOND_REQUEST_NOT_ALLOWED_FOR_INFORMAL_ENTRY,
    FORMAL_ENTRY_REQUIRED_FOR_CBP_ASSIGNED_NUMBER,
    ARRIVAL_DATE_TOO_EARLY,
    ACH_METHOD_NOT_ALLOWED_WITH_PUN,
    ACH_METHOD_NOT_ALLOWED_WITHOUT_PUN,
  ];
  private readonly missingFieldsOnShipment: string[] = [EXPORT_DATE_MISSING, FOREIGN_PORT_MISSING, EXPORT_DATE_LATER_THAN_ARRIVAL_DATE];
  private ports: PortModel[] = [];

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private router: Router,
    private codeRepository: CodeRepository,
    private entrySummaryService: EntrySummaryService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private companiesService: CompaniesService,
  ) {}

  public ngOnInit(): void {
    this.getPorts();
    this.checkIsMissing();
    this.getEnteredValue();
    this.updateBondTypeLabel();
  }

  public ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges.entrySummary) {
      this.getPorts();
      this.checkIsMissing();

      this.bond = this.entrySummary ? (this.entrySummary.bondRequest ? null : this.entrySummary.bondInfo ?? null) : null;
      this.bondRequest =
        this.entrySummary && this.entrySummary.bondRequest && this.entrySummary.bondRequest.state !== enumBondRequestState.COMPLETED
          ? this.entrySummary.bondRequest
          : null;
      this.getPort();
      this.getEnteredValue();
      this.updateBondTypeLabel();
    }

    if (simpleChanges.allValidationMessages) {
      this.checkIsMissing();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public get portOfEntry(): PortModel {
    return this.ports.find((port: PortModel) => port.code === this.entrySummary.portOfEntryCode);
  }

  public get portOfUnlading(): PortModel {
    return this.ports.find((port: PortModel) => port.code === this.entrySummary.portOfUnladingCode);
  }

  public get isAir(): boolean {
    return this.entrySummary.transportationMode === enumTransportationMode.AIR;
  }

  public get isOcean(): boolean {
    return this.entrySummary.transportationMode === enumTransportationMode.OCEAN;
  }

  public get isRail(): boolean {
    return this.entrySummary.transportationMode === enumTransportationMode.RAIL;
  }

  public get isTruck(): boolean {
    return this.entrySummary.transportationMode === enumTransportationMode.TRUCK;
  }

  /** Details pencil: per-pencil override ?? isCanEdit; a Truck ES is editable only where the Truck tile exists (#13261). */
  public get isCanEditDetails(): boolean {
    return (this.canEditDetails ?? this.isCanEdit) && (!this.isTruck || this.isTruckModeAvailable);
  }

  public get isWithVia(): boolean {
    return this.entrySummary?.portOfEntryCode !== this.entrySummary?.portOfUnladingCode;
  }

  public get hasShipmentError(): boolean {
    if (this.entrySummary?.type === enumEntrySummaryType.FORMAL && !this.entrySummary?.bondInfo && !this.entrySummary?.bondRequest) {
      return true;
    }

    return false;
  }

  public getValidationText(field: string): string {
    if (!this.bond || !this.bond.validationMessages || !this.bond.validationMessages[field] || !this.bond.validationMessages[field].text) {
      return null;
    }
    return `BACK_ERROR.${this.bond.validationMessages[field].text}`;
  }

  public get singleBondText(): string {
    if (!this.bondRequest?.customState?.header) {
      return 'INFO.BN.SINGLE_BOND_PURCHASE_REQUESTED';
    }

    switch (this.bondRequest?.customState?.header) {
      case CustomState.CANCELED:
        return 'INFO.BN.SINGLE_BOND_PURCHASE_CANCELED';
      case CustomState.CANCELING:
        return 'INFO.BN.SINGLE_BOND_PURCHASE_CANCELING';
      case CustomState.REFUNDED:
        return 'INFO.BN.SINGLE_BOND_PURCHASE_REFUNDED';
      default:
        return 'INFO.BN.SINGLE_BOND_PURCHASE_REQUESTED';
    }
  }

  public importerSameAsCompany(): string {
    const savedEntity: EntityModel = this.entrySummary?.importer;

    if (!this.company || !savedEntity) {
      return '';
    }

    if (
      this.company.identifier === savedEntity.entityIdentifier &&
      this.company.identifierQualifier === savedEntity.entityIdentifierQualifier &&
      this.company.businessName === savedEntity.name &&
      this.company?.address?.line1 === savedEntity?.address?.line1 &&
      this.company?.address?.line2 === savedEntity?.address?.line2 &&
      this.company?.address?.line3 === savedEntity?.address?.line3 &&
      this.company?.address?.city === savedEntity?.address?.city &&
      this.company?.address?.state?.id === savedEntity?.address?.state?.id &&
      this.company?.address?.postalCode === savedEntity?.address?.postalCode
    ) {
      const entityName: string = this.translateService.instant('LABELS.IOR_PROFILE');
      return this.translateService.instant('LABELS.SAME_AS', { entityName });
    }
  }

  public get isAllFromFieldsMissing(): boolean {
    return this.isHasEsOcrRedesign && !!this.foreignPortErrorText && !!this.exportDateErrorText;
  }

  public get isAllToFieldsMissing(): boolean {
    return (
      this.isHasEsOcrRedesign &&
      !!this.locationErrorText &&
      !!this.entryErrorText &&
      !!this.destinationStateErrorText &&
      !this.entrySummary.arrivalDate
    );
  }

  public get isAllViaFieldsMissing(): boolean {
    return this.isHasEsOcrRedesign && !this.portOfUnlading && !this.portOfUnlading?.description && !this.portOfUnlading?.code;
  }

  public get isAnyFromFieldsMissing(): boolean {
    return this.isHasEsOcrRedesign && (!!this.foreignPortErrorText || !!this.exportDateErrorText);
  }

  public get isAnyToFieldsMissing(): boolean {
    return (
      this.isHasEsOcrRedesign &&
      (!!this.locationErrorText || !!this.entryErrorText || !!this.destinationStateErrorText || !this.entrySummary.arrivalDate)
    );
  }

  public get isHasEsOcrRedesign(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.esOcrRedesign);
  }

  public get foreignPortErrorText(): string {
    const firstShipment: ShipmentModel = this.entrySummary.shipments[0];
    const error: ValidationMessageModel = firstShipment?.validationMessages?.find(
      (item: ValidationMessageModel) => item.text === FOREIGN_PORT_MISSING,
    );

    return error ? `BACK_ERROR.FOREIGN_PORT_MISSING` : null;
  }

  public get locationErrorText(): string {
    const error: string = this.findError(LOCATION_MISSING);

    return error ? `BACK_ERROR.${error}` : null;
  }

  public get unladingErrorText(): string {
    const error: string = this.findError(PORT_OF_UNLADING_MISSING);

    return error ? `BACK_ERROR.${error}` : null;
  }

  public get entryErrorText(): string {
    const error: string = this.findError(PORT_OF_ENTRY_MISSING);

    return error != null ? `BACK_ERROR.${error}` : null;
  }

  public get destinationStateErrorText(): string {
    const error: string = this.findError(STATE_OF_DESTINATION_MISSING);

    return error ? `BACK_ERROR.${error}` : null;
  }

  public get arrivalDateErrorText(): string {
    const error: string = this.findError(ARRIVAL_DATE_MISSING) || this.findError(ARRIVAL_DATE_TOO_EARLY);

    return error ? `BACK_ERROR.${error}` : null;
  }

  public get suretyCodeErrorText(): string {
    const error: string = this.findError(BOND_SURETY_CODE_MISSING);

    return error ? `BACK_ERROR.${error}` : null;
  }

  public get referenceNumberErrorText(): string {
    const error: string = this.findError(BOND_REFERENCE_NUMBER_MALFORMED);

    return error ? `BACK_ERROR.${error}` : null;
  }

  public get bondTypeErrorText(): string {
    const error: string = this.findError(BOND_TYPE_MISSING);

    return error ? `BACK_ERROR.${error}` : null;
  }

  public get exportDateErrorText(): string {
    let error: string = null;
    if (!this.exportDate) {
      error = EXPORT_DATE_MISSING;
    } else if (this.findShipmentError(EXPORT_DATE_LATER_THAN_ARRIVAL_DATE)) {
      error = EXPORT_DATE_LATER_THAN_ARRIVAL_DATE;
    }

    return error ? `BACK_ERROR.${error}` : null;
  }

  public get isShowDutiesPaymentType(): boolean {
    return this.entrySummary.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED && !this.isHasActiveACH;
  }

  public get isHasActiveACH(): boolean {
    return this.companiesService.isHasActiveACH;
  }

  public get isShipmentValid(): boolean {
    return !this.entrySummary.validationMessages.length;
  }

  public get bondInfo(): BondModel {
    if (!this.entrySummary) {
      return;
    }

    if (!!this.entrySummary?.bondRequest && !!this.entrySummary?.bondInfo) {
      return this.checkBondInfo(this.entrySummary.bondInfo) ? null : this.entrySummary.bondInfo;
    } else if (!this.entrySummary?.bondRequest && !!this.entrySummary?.bondInfo) {
      return this.entrySummary.bondInfo;
    }
  }

  private updateBondTypeLabel(): void {
    if (!this.bondInfo) {
      this.bondTypeLabel = '';
      return;
    }
    this.isSingleBondType = this.bondInfo.type === enumBondType.SINGLE_TRANSACTION;
    this.bondTypeLabel =
      this.bondInfo.type === enumBondType.SINGLE_TRANSACTION
        ? 'FORM_LABEL.CUSTOM_BOND_SINGLE'
        : this.bondInfo.type === enumBondType.CONTINUOUS
        ? 'FORM_LABEL.CUSTOM_BOND_CONTINUES'
        : '';
  }

  private checkBondInfo(bondInfo: BondModel): boolean {
    let res: boolean = true;
    const requiredFields: string[] = ['suretyCode', 'type', 'bondAmount', 'referenceNumber'];

    for (const field of requiredFields) {
      if (![null, undefined, ''].includes(bondInfo[field])) {
        res = false;
      }
    }

    return res;
  }

  public get singleBondRequest(): BondRequestModel {
    return this.entrySummary &&
      this.entrySummary.bondRequest &&
      this.entrySummary.bondRequest.bondType === enumBondType.SINGLE_TRANSACTION &&
      this.entrySummary.bondRequest.state !== enumBondRequestState.COMPLETED
      ? this.entrySummary.bondRequest
      : null;
  }

  public get continuousBondRequest(): BondRequestModel {
    return this.entrySummary &&
      this.entrySummary.bondRequest &&
      this.entrySummary.bondRequest.bondType === enumBondType.CONTINUOUS &&
      this.entrySummary.bondRequest.state !== enumBondRequestState.COMPLETED
      ? this.entrySummary.bondRequest
      : null;
  }

  public get hasCanceledBondRequest(): boolean {
    return (
      this.entrySummary && this.entrySummary.bondRequest && this.entrySummary.bondRequest?.customState?.header === CustomState.CANCELED
    );
  }

  public get sumValidation(): string {
    let result: string = null;

    const allLineItems: LineModel[] = this.getAllLines();
    const isHasChina: boolean = allLineItems
      .map((line: LineModel) => line.countryOfOrigin?.code)
      .some((code: string) => code === CHINA_COUNTRY_CODE);
    const limit: number = isHasChina ? ENTERED_VALUE_CHINA_LIMIT : ENTERED_VALUE_TOTAL_LIMIT;
    // #13284: no "switch to Informal" banner for an ANI importer — Informal is not an option for it.
    if (
      this.entrySummary.type === enumEntrySummaryType.FORMAL &&
      !isCbpAssignedNumberImporter(this.entrySummary.importer) &&
      this.enteredValue <= limit
    ) {
      result = FORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_SMALL_DESKTOP;
    }
    if (this.entrySummary.type === enumEntrySummaryType.INFORMAL && this.enteredValue > limit) {
      const isNonDraft = this.entrySummary.state !== enumEntrySummaryState.NOT_SENT;
      result = isNonDraft
        ? INFORMAL_NON_DRAFT_ES_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG_DESKTOP
        : INFORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG_DESKTOP;
    }
    return result;
  }

  private getAllLines(): LineModel[] {
    const allShipments: ShipmentModel[] = this.entrySummary?.shipments || [];
    const allInvoices: InvoiceModel[] = flatten(allShipments.map((shipment: ShipmentModel) => shipment.invoices || []));
    return flatten(allInvoices.map((invoice: InvoiceModel) => invoice.lines || []));
  }

  private findError(field: string): string {
    const errors: ValidationMessageModel[] = this.entrySummary?.validationMessages || [];

    return errors.find((item: ValidationMessageModel) => item.text === field)?.text;
  }

  private findShipmentError(field: string): string {
    const errors: ValidationMessageModel[] = this.entrySummary?.shipments?.[0]?.validationMessages || [];

    return errors.find((item: ValidationMessageModel) => item.text === field)?.text;
  }

  private getPorts(): void {
    if (!this.entrySummary) {
      return;
    }

    const { hasSingleExportData, shipments, portOfEntryCode, portOfUnladingCode, locationCode, stateOfDestination } = this.entrySummary;

    if (!hasSingleExportData && shipments && shipments[0]) {
      const { exportDate, foreignPortOfLadingId } = shipments[0];
      this.exportDate = exportDate;

      if (foreignPortOfLadingId && (!this.foreignPort || foreignPortOfLadingId !== this.foreignPort.id)) {
        this.codeRepository
          .getForeignPort(foreignPortOfLadingId)
          .pipe(takeUntil(this.destroy$))
          .subscribe((port: ForeignPortModel) => {
            this.foreignPort = port;

            // TODO investigate why new the values are not immediately rendered
            this.cdr.markForCheck();
          });
      }
    } else {
      this.foreignPort = null;
      this.exportDate = null;
    }

    const filterCode: (code: string) => boolean = (code: string) => code && !this.ports.find((exPort: PortModel) => exPort.code === code);
    const portsForSearch: string[] = UtilsHelper.uniq<string>([portOfEntryCode, portOfUnladingCode]).filter(filterCode);

    if (portsForSearch.length) {
      forkJoin(portsForSearch.map((code: string) => this.codeRepository.getDomesticPort(code)))
        .pipe(takeUntil(this.destroy$))
        .subscribe((resp: PortModel[]) => {
          // Merge (not replace): portsForSearch excludes already-cached codes, so `this.ports = resp` would drop
          // the cached port and make portOfEntry/portOfUnlading toggle missing on alternate reloads (#13102).
          this.ports = UtilsHelper.uniqBy<PortModel>([...this.ports, ...resp], 'code');

          this.cdr.markForCheck();
        });
    }

    if (locationCode && (!this.location || this.location.code !== locationCode)) {
      this.codeRepository
        .getLocation(locationCode)
        .pipe(takeUntil(this.destroy$))
        .subscribe((location: LocationOfGoodsModel) => {
          this.location = location;
          this.cdr.markForCheck();
        });
    } else if (locationCode == null) {
      this.location = null;
    }

    if (stateOfDestination && (!this.stateOfDestination || this.stateOfDestination.code !== stateOfDestination)) {
      this.codeRepository
        .getState(stateOfDestination)
        .pipe(takeUntil(this.destroy$))
        .subscribe((state: StateModel) => {
          this.stateOfDestination = state;
          this.cdr.markForCheck();
        });
    }
  }

  private checkIsMissing(): void {
    const firstShipment: ShipmentModel = this.entrySummary.shipments[0];
    this.currentCardValidationMessages = [];

    for (const mess of this.allValidationMessages) {
      if (
        this.missingFields.some((error: string) => mess.text.endsWith(error)) ||
        (!this.entrySummary.hasSingleExportData && this.missingFieldsOnShipment.some((error: string) => mess.text.endsWith(error)))
      ) {
        this.currentCardValidationMessages.push(mess);
      }
    }

    const requiredFields: string[] = [
      'arrivalDate',
      'locationCode',
      'portOfEntryCode',
      'portOfUnladingCode',
      'trip',
      'vesselName',
      'payerUnitNumber',
      'dutiesPaymentType',
    ];

    const requiredFieldsOnShipment: string[] = this.entrySummary.hasSingleExportData ? [] : ['foreignPortOfLadingId', 'exportDate'];

    this.isEmpty = true;

    for (const field of requiredFields) {
      if (![null, undefined, ''].includes(this.entrySummary[field])) {
        this.isEmpty = false;
      }
    }

    if (firstShipment) {
      for (const field of requiredFieldsOnShipment) {
        if (![null, undefined, ''].includes(firstShipment[field])) {
          this.isEmpty = false;
        }
      }
    }
  }

  private getPort(): void {
    if (this.bondRequest && this.bondRequest.portCode) {
      this.codeRepository
        .getDomesticPort(this.bondRequest.portCode)
        .pipe(takeUntil(this.destroy$))
        .subscribe((port: PortModel) => {
          this.arrivalPort = port;

          this.cdr.markForCheck();
        });
    }
  }

  private getEnteredValue(): void {
    this.entrySummaryService
      .getEntrySummaryEnteredValue(this.entrySummary)
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: number) => {
        this.enteredValue = value;
      });
  }
}
